import { useCallback, useEffect, useState } from 'react';
import {
  listOperations,
  createOperation as apiCreate,
  parseQuickAdd as apiParse,
  updateOperation as apiUpdate,
  snoozeOperation as apiSnooze,
  completeOperation as apiComplete,
  acceptMiloSuggestion as apiAccept,
  dismissOperation as apiDismiss,
  deleteOperation as apiDelete,
} from '../../../data/api';

/**
 * useOperations — state management for the Operations Queue.
 *
 * Loads operations on mount. Exposes:
 *   - operations (list)
 *   - counts (by state)
 *   - loading / busy / error
 *   - actions: create, update, snooze, complete, accept, dismiss, parse, refresh
 *
 * Optimistic update strategy: each action does an optimistic mutation in
 * local state, then revalidates by re-fetching on success or rolling back
 * on failure.
 */
export function useOperations() {
  const [operations, setOperations] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listOperations();
      setOperations(res.operations || []);
      setCounts(res.counts || {});
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to load operations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const wrap = (fn) => async (...args) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn(...args);
      await refresh();
      return res;
    } catch (err) {
      setError(err?.response?.data?.detail || 'Operation failed.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const actions = {
    create:   wrap(apiCreate),
    update:   wrap(apiUpdate),
    snooze:   wrap(apiSnooze),
    complete: wrap(apiComplete),
    accept:   wrap(apiAccept),
    dismiss:  wrap(apiDismiss),
    remove:   wrap(apiDelete),    // archive (soft-delete)
    parse:    apiParse,           // pure read; no refresh
    refresh,
  };

  return { operations, counts, loading, busy, error, actions };
}
